import React, { useState } from 'react';
import {
  ActivityIndicator, KeyboardAvoidingView, Platform,
  Pressable, Modal,
  StyleSheet, Text, TextInput, TouchableOpacity, View,
  Keyboard,
  ScrollView,
} from 'react-native';
import { FontAwesome } from '@expo/vector-icons';
import Colors from '../../constants/Colors';
import Style from '../../constants/Style';
import CloseModalIcon from '../CloseModalIcon';
import AlertModal from '../AlertModal';
import createUser from '../../requests/Users/CreateUser';
import { emailRegex } from '../Regex';
import { logEvent } from '../LogEvent';
import isMobile from '../IsMobile';
import PressableStyled from '../PressableStyled';
import Button from '../Button';

interface InviteUserModalProps {
  modalVisible: any;
  users: any;
  // eslint-disable-next-line no-unused-vars
  setModalVisible(value: any): any;
  // eslint-disable-next-line no-unused-vars
  invitedUser: (name: string) => void;
  roles?: any[];
}

const InviteUserModal = ({
  modalVisible, setModalVisible, invitedUser, users, roles = [],
}: InviteUserModalProps) => {
  const [firstNameFocused, setFirstNameFocused] = useState<boolean>(false);
  const [secondNameFocused, setSecondNameFocused] = useState<boolean>(false);
  const [emailFocused, setEmailFocused] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [titleValid, setTitleValid] = useState<boolean>(true);
  const [emailValid, setEmailValid] = useState<boolean>(false);
  const [firstNameValue, setFirstNameValue] = useState<string>('');
  const [secondNameValue, setSecondNameValue] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [roleDropdownVisible, setRoleDropdownVisible] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedRoleName, setSelectedRoleName] = useState(null);

  const maxTitleLength: number = 40;

  const [alertVisible, setAlertVisible] = useState<boolean>(false);
  const [alertError, setAlertError] = useState<boolean>(true);
  const [alertTitle, setAlertTitle] = useState<string>('');
  const [alertMessage, setAlertMessage] = useState<string>('');

  const showAlert = (title: string, error: boolean = true, message: string = '') => {
    setAlertTitle(title);
    setAlertError(error);
    setAlertMessage(message);
    setAlertVisible(true);
  };

  const getTestID = (role) => {
    switch (role) {
      case 'Admin':
        return 'um-admin-role-btn';
      case 'Non Admin':
        return 'um-non-admin-role-btn';
      default:
        return 'um-other-role-btn';
    }
  };

  const restoreDefaults: () => void = () => {
    setFirstNameFocused(false);
    setSecondNameFocused(false);
    setEmailFocused(false);
    setSaving(false);
    setTitleValid(true);
    setEmailValid(false);
    setFirstNameValue('');
    setSecondNameValue('');
    setEmail('');
    setSelectedRole(null);
    setSelectedRoleName(null);
  };

  function validateTitle(title) {
    let valid = true;
    if (
      title === undefined
      || title === null
      || title?.trim().length === 0
      || title?.trim().length > maxTitleLength
    ) {
      valid = false;
      setTitleValid(false);
    } else {
      setTitleValid(true);
    }
    return valid;
  }

  const validateEmail = (text: string) => {
    const isValid: boolean = text?.length > 0 && new RegExp(emailRegex.exp).test(text);
    setEmailValid(isValid);
    return isValid;
  };

  const save: () => void = () => {
    logEvent('InviteUserModal', 'Send');
    const emails: string[] = users.map((user) => user.email?.trim().toLowerCase());
    if (emails.indexOf(email.trim().toLowerCase()) >= 0) {
      showAlert(
        'Oops! This user is already invited or signed up',
        true,
        'It looks like this user has already been invited or is already a part of our system. Please review the details or let us know if you need help!',
      );
      return;
    }
    if (titleValid && emailValid) {
      setSaving(true);
      const userIds = [];
      const deletedUserIds = [];
      users.forEach((user) => {
        if (user.selected) {
          userIds.push(user.ClientIdEmail);
        } else if (user.deleted) {
          deletedUserIds.push(user.ClientIdEmail);
        }
      });
      createUser(
        email,
        firstNameValue?.trim(),
        secondNameValue?.trim(),
        selectedRole === 'Admin',
        (selectedRole === 'Admin') ? null : selectedRole,
      ).then((res) => {
        if (res?.isError) {
          setSaving(false);
          showAlert('Error', true, res.errorMessage);
        } else {
          setSaving(false);
          restoreDefaults();
          invitedUser(firstNameValue ? `${firstNameValue} ${secondNameValue}` : email);
        }
      });
    }
  };

  const cancel: () => void = () => {
    logEvent('InviteUserModal', 'Cancel');
    restoreDefaults();
    setModalVisible(false);
  };

  const renderDetails = () => (
    <View style={{ marginHorizontal: isMobile ? 16 : 0 }}>
      <View style={{ flexDirection: 'row' }}>
        <View style={{ flex: 1 }}>
          <Text style={styles.textBoxHeaders}>{'First name'}</Text>
          <TextInput
            style={[
              styles.noteText,
              {
                borderColor: firstNameFocused ? Colors.light.hereworksBlue300
                  : Colors.light.hereworksBlue100,
              },
            ]}
            placeholderTextColor={Colors.light.hereworksBlack400}
            placeholder={'First name'}
            onFocus={() => setFirstNameFocused(true)}
            onBlur={() => setFirstNameFocused(false)}
            value={firstNameValue ?? ''}
            onChangeText={(text: string) => {
              validateTitle(text);
              setFirstNameValue(text);
            }}
          />
          {firstNameValue?.trim().length > maxTitleLength && (
            <Text style={[styles.validationText, { color: Colors.light.hereworksUrgentRed }]}>
              {`${firstNameValue?.trim().length ?? 0}/${maxTitleLength}`}
            </Text>
          )}
        </View>
        <View style={{ flex: 1, marginLeft: 8 }}>
          <Text style={styles.textBoxHeaders}>{'Second name'}</Text>
          <TextInput
            style={[
              styles.noteText,
              {
                borderColor: secondNameFocused ? Colors.light.hereworksBlue300
                  : Colors.light.hereworksBlue100,
              },
            ]}
            placeholderTextColor={Colors.light.hereworksBlack400}
            placeholder={'Second name'}
            onFocus={() => setSecondNameFocused(true)}
            onBlur={() => setSecondNameFocused(false)}
            value={secondNameValue ?? ''}
            onChangeText={(text) => {
              validateTitle(text);
              setSecondNameValue(text);
            }}
          />
          {secondNameValue?.trim().length > maxTitleLength && (
            <Text style={[styles.validationText, { color: Colors.light.hereworksUrgentRed }]}>
              {`${secondNameValue?.trim().length ?? 0}/${maxTitleLength}`}
            </Text>
          )}
        </View>
      </View>
      <Text style={styles.textBoxHeaders}>{'Email*'}</Text>
      <TextInput
        style={[
          styles.noteText,
          {
            borderColor: emailFocused ? Colors.light.hereworksBlue300
              : Colors.light.hereworksBlue100,
          },
        ]}
        placeholderTextColor={Colors.light.hereworksBlack400}
        placeholder={'Email'}
        onFocus={() => setEmailFocused(true)}
        onBlur={() => setEmailFocused(false)}
        value={email ?? ''}
        onChangeText={(text) => {
          validateEmail(text);
          setEmail(text);
        }}
      />
      <>
        <Text style={styles.textBoxHeaders}>Role*</Text>
        <View style={styles.roleDropdownContainer}>
          <TouchableOpacity
            style={styles.dropdownBox}
            onPress={() => {
              setRoleDropdownVisible(!roleDropdownVisible);
              Keyboard.dismiss();
              logEvent('EditUser', `Role_dropdown_${roleDropdownVisible ? 'Close' : 'Open'}`);
            }}
          >
            <Text style={styles.dropdownBoxText}>{selectedRoleName ?? 'Choose role'}</Text>
            <View style={{ flex: 1, alignItems: 'flex-end' }}>
              <FontAwesome
                size={20}
                style={{
                  color: Colors.light.hereworksLightBlue,
                  marginRight: 12,
                }}
                name={roleDropdownVisible ? 'angle-up' : 'angle-down'}
              />
            </View>
          </TouchableOpacity>
          {roleDropdownVisible && (
            <View style={styles.dropdownView}>
              <ScrollView>
                <Pressable
                  key={0}
                  onPress={() => {
                    setSelectedRole('Admin');
                    setSelectedRoleName('Admin');
                    setRoleDropdownVisible(!roleDropdownVisible);
                    logEvent('EditUser', 'Selected_group');
                  }}
                  testID={getTestID('Admin')}
                >
                  <Text style={styles.dropdownItemText}>Admin</Text>
                </Pressable>
                {roles.map((role, key) => (
                  <Pressable
                    key={key}
                    onPress={() => {
                      setSelectedRole(role.id);
                      setSelectedRoleName(role.name);
                      setRoleDropdownVisible(!roleDropdownVisible);
                      logEvent('EditUser', 'Selected_group');
                    }}
                    testID={getTestID(role.name)}
                  >
                    <Text style={styles.dropdownItemText}>{role.name}</Text>
                  </Pressable>
                ))}
              </ScrollView>
            </View>
          )}
        </View>
      </>
    </View>
  );

  const renderSendCancel = () => <View style={styles.buttonContainer}>
    <Button
      title='Cancel'
      onPress={cancel}
    />
    <Button
      disabled={!(titleValid && emailValid && selectedRole) || saving}
      title='Send'
      onPress={save}
      children={saving && <ActivityIndicator style={styles.savingIndicator} />}
      affirmative
      testID='send-btn'
    />
  </View>;

  const renderModalContent = () => (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'position' : 'height'}
      style={{ flex: isMobile ? undefined : 1, alignSelf: 'stretch', justifyContent: isMobile ? 'flex-start' : 'center' }}>
      <View style={styles.container}>
        <Text style={styles.title}>
          {'Invite User'}
        </Text>
        <Text style={styles.description}>
          {'Fields marked with * are mandatory'}
        </Text>
        <View
          style={{
            marginHorizontal: isMobile ? 0 : 40,
            alignSelf: 'stretch',
            zIndex: 2,
          }}
        >
          {renderDetails()}
        </View>
        {renderSendCancel()}
        <PressableStyled
          onPress={() => {
            logEvent('InviteUserModal', 'Close_Modal');
            restoreDefaults();
            setModalVisible(false);
          }}
          style={Style.closeButton}
        >
          <CloseModalIcon />
        </PressableStyled>
      </View>
    </KeyboardAvoidingView>
  );

  if (isMobile) return renderModalContent();

  return (
    <Modal
      visible={modalVisible}
      style={{ alignItems: 'center', flex: 1, justifyContent: isMobile ? 'flex-start' : 'center' }}
      onRequestClose={() => {
        setModalVisible(false);
        restoreDefaults();
      }}
      animationType={isMobile ? 'slide' : 'fade'}
      presentationStyle='pageSheet'
      transparent={!isMobile}
    >
      {isMobile ? (
        renderModalContent()
      ) : <Pressable
          onPress={() => setModalVisible(false)}
          style={{
            flex: 1, justifyContent: 'center', paddingHorizontal: 40, backgroundColor: '#00000030',
          }}
        >
          <Pressable onPress={(e) => e.stopPropagation()} style={{ cursor: 'auto' }}>
            {renderModalContent()}
          </Pressable>
        </Pressable>}
      <AlertModal
        modalVisible={alertVisible}
        error={alertError}
        title={alertTitle}
        message={alertMessage}
        setModalVisible={setAlertVisible}
      />
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: isMobile ? 'stretch' : 'center',
    borderRadius: 10,
    backgroundColor: '#fff',
    marginTop: isMobile ? 16 : 0,
    minWidth: isMobile ? undefined : 400,
  },
  noteText: {
    color: Colors.light.hereworksBlack600,
    alignSelf: 'stretch',
    fontFamily: 'Roboto_400Regular',
    alignItems: 'center',
    fontSize: 16,
    lineHeight: Platform.OS === 'ios' ? undefined : 24,
    backgroundColor: Colors.light.plainBackground,
    borderWidth: 1,
    alignContent: 'center',
    marginVertical: 5,
    paddingVertical: 10,
    paddingHorizontal: 8,
    borderRadius: 5,
    verticalAlign: 'top',
    outlineWidth: 0,
  },
  textBoxHeaders: {
    marginTop: 12,
    color: Colors.light.hereworksBlack400,
    fontFamily: 'Roboto_400Regular',
    fontSize: 16,
  },
  radioBoxHeaders: {
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Roboto_400Regular',
    fontSize: 16,
  },
  dropdownBox: {
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksPaleBlue900,
    borderRadius: 10,
    marginTop: 5,
    marginBottom: 2,
    alignSelf: 'stretch',
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    zIndex: 2,
  },
  dropdownBoxText: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 14,
    color: Colors.light.hereworksBlack600,
    marginVertical: 9.5,
    marginLeft: 12,
    flex: 1,
  },
  roleDropdownContainer: {
    position: 'relative',
    zIndex: 10,
  },
  validationText: {
    marginTop: 4,
    color: Colors.light.hereworksBlack300,
    fontFamily: 'Roboto_400Regular',
    fontSize: 12,
    lineHeight: 20,
  },
  description: {
    fontSize: 16,
    lineHeight: 24,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
  },
  buttonContainer: {
    flexDirection: 'row',
    marginVertical: isMobile ? 25 : 40,
    zIndex: 1,
    marginHorizontal: isMobile ? 16 : 0,
  },
  savingIndicator: {
    marginStart: 12,
  },
  title: {
    marginTop: isMobile ? 19 : 60,
    marginBottom: isMobile ? 19 : 32,
    marginHorizontal: isMobile ? 104 : 0,
    fontSize: isMobile ? 20 : 28,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksBlack600,
  },
  dropdownView: {
    position: 'absolute',
    top: 52,
    left: 0,
    right: 0,
    zIndex: 20,
    elevation: 20,
    maxHeight: 150,
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksPaleBlue900,
    backgroundColor: '#fff',
  },
  dropdownItemText: {
    marginLeft: 12,
    marginVertical: 9,
    fontFamily: 'Poppins_400Regular',
    fontSize: 14,
    color: Colors.light.hereworksPrimaryGrey,
  },
});

export default InviteUserModal;
